import { faCheckCircle } from '@fortawesome/free-regular-svg-icons'
import { faEdit, faRotateBack } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { UseMutationResult } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import React, { useMemo, useRef, useState } from 'react'
import { Cell, FieldError, Row, TextField } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { LanguagesConfig, Recipe, RecipeTag, TempRecipe } from '@/api/types'
import { Button } from '@/components/Button'
import { Flex } from '@/components/Flex'
import { Input } from '@/components/Input'
import { Select } from '@/components/Select'
import { Spinner } from '@/components/Spinner'
import { NutritionalValueCalculator } from '@/features/NutritionFacts'
import { roundNumber } from '@/utils'
import { GroupedTagsType, getUserFullName, groupTags } from '../helpers'

function recipeHasLang(recipe: Recipe, lang: string): boolean {
  return recipe.availableTranslations?.some((l) => l === lang) ?? false
}

type RecipeRowProps = {
  id: number
  recipe: Recipe
  groupedTags: GroupedTagsType
  saveRecipe: UseMutationResult
  lang?: string
  languages?: LanguagesConfig
}

export function RecipeRow({ recipe, saveRecipe, groupedTags, lang, languages }: RecipeRowProps) {
  const rowRef = useRef<HTMLTableRowElement>(null)
  const [tempRecipe, setTempRecipe] = useState<TempRecipe>(recipe)
  const [isInvalid, setIsInvalid] = useState(false)
  const [onEditMode, setOnEditMode] = useState(false)
  const [activeRow, setActiveRow] = useState<number | null>(null)
  const { t } = useTranslation()
  const langs = languages?.languages ?? []

  const isDirty = tempRecipe.name !== recipe.name
  const isActiveRow = activeRow === parseInt(rowRef?.current?.dataset.key ?? '')

  // When showing the translation, we are disabling editing the name from the table
  // It would be nice to allow editing the translation as well from here
  const isTranslation = lang && lang !== 'en'

  const recipeTags = groupTags(tempRecipe.tags)

  const resetName = () => {
    focusInput()

    setIsInvalid(false)
    setTempRecipe({
      ...tempRecipe,
      name: recipe.name,
    })
  }

  const updateTempName = (name: string) => {
    setTempRecipe({
      ...tempRecipe,
      name,
    })

    if (name.length === 0) {
      setIsInvalid(true)
    } else {
      setIsInvalid(false)
    }
  }

  const handleChangeTags = (selection: RecipeTag[]) => {
    setTempRecipe({
      ...tempRecipe,
      tags: selection,
    })

    saveRecipe.mutate({ ...recipe, tags: selection })
  }

  const handleInputBlur = () => {
    if (isDirty && !isInvalid) {
      setActiveRow(recipe.idRecipe)
      saveRecipe.mutate(
        { ...recipe, name: tempRecipe.name },
        {
          onSuccess: () => {
            setActiveRow(null)
          },
        },
      )
    }

    if (!isInvalid) {
      setOnEditMode(false)
    }
  }

  const handleKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter') {
      const input = rowRef.current?.querySelector('input')
      input?.blur()

      setOnEditMode(false)
    }
  }

  const enterEditMode = () => {
    setOnEditMode(true)

    focusInput()
  }

  const focusInput = () => {
    const input = rowRef.current?.querySelector('td:first-child') as HTMLInputElement
    input?.focus()
  }

  const energy = useMemo(
    () =>
      roundNumber(
        NutritionalValueCalculator.fromRecipeIngredients(
          recipe.recipeIngredients,
          recipe.preparationDetails?.servings,
        ).energy,
      ),
    [recipe.recipeIngredients, recipe.preparationDetails?.servings],
  )

  return (
    <Row id={recipe.idRecipe} ref={rowRef} dependencies={[recipe]}>
      <Cell>
        {onEditMode ? (
          <TextField
            aria-label="Name"
            isInvalid={isInvalid}
            value={tempRecipe.name}
            onKeyDown={(ev) => handleKeyDown(ev)}
            onChange={(value) => updateTempName(value)}
            onBlur={handleInputBlur}>
            <Input />

            <FieldError>
              <div className="RecipeCell-empty RecipeCell-status">
                <Button variant="icon" onPress={resetName}>
                  <FontAwesomeIcon icon={faRotateBack} color="var(--gray-400)" />
                </Button>
              </div>
            </FieldError>
          </TextField>
        ) : (
          <Link
            to="/recipes/$recipeId"
            params={{ recipeId: recipe.idRecipe.toString() }}
            search={{ lang }}
            className="RecipeCell-link">
            {isTranslation ? recipe.name : tempRecipe.name}
            {!isTranslation && (
              <div className="RecipeCell-status RecipeCell-edit">
                <Button variant="icon" onPress={enterEditMode}>
                  <FontAwesomeIcon icon={faEdit} color="var(--gray-400)" />
                </Button>
              </div>
            )}
          </Link>
        )}
        {isActiveRow && (
          <div className="RecipeCell-status">{saveRecipe.isPending && <Spinner />}</div>
        )}
      </Cell>

      <Cell>
        <Select
          isMulti
          isClearable={false}
          options={groupedTags.MEAL_TYPE}
          value={recipeTags.MEAL_TYPE}
          getOptionLabel={(option) => t(`recipe.mealTypeOptions.${option.name}` as any)}
          getOptionValue={(option) => option.name}
          placeholder={t('recipe.mealTypePlaceholder')}
          onChange={handleChangeTags}
          components={{
            DropdownIndicator: null,
          }}
          classNames={{
            control: () => 'RecipeTable-selectControl',
          }}
        />
      </Cell>
      {langs.map((lang) => (
        <Cell key={lang.code}>
          {recipeHasLang(recipe, lang.code) && (
            <FontAwesomeIcon icon={faCheckCircle} color="var(--highlight-background)" />
          )}
        </Cell>
      ))}
      <Cell>{getUserFullName(recipe.author)}</Cell>
      <Cell>{energy}</Cell>
      <Cell className="react-aria-Cell RecipeIndex-editButton">
        <Button asChild>
          <Link
            to="/recipes/$recipeId"
            params={{ recipeId: recipe.idRecipe.toString() }}
            search={{ lang }}>
            <Flex gap={8} align="center">
              <FontAwesomeIcon icon={faEdit} color="var(--gray-400)" />
              {t('edit')}
            </Flex>
          </Link>
        </Button>
      </Cell>
    </Row>
  )
}
